/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { AutomationRuleActionContextType, ActionItemType, ActionValidationErrors, EditWorkItemActionType, SendNotificationToSlackActionType, SendEmailNotificationActionType, SendTeamsNotificationActionType, AddCommentToWorkItemActionType, AddCommentInputDataType, SendEmailNotificationInputDataType, SendSlackNotificationInputDataType, SendTeamsNotificationInputDataType } from './types';
import { randomStringGenerator } from '../utils/random-string-generator';
import { stringRequired } from '../utils/validations';
import { actionTypes } from '.';
import { useEditWorkItem } from './edit-work-item/use-edit-work-item';
import { useAddCommentToWorkItem } from './add-comment/use-add-comment';
import { useSendNotificationToSlack } from './send-notification-to-slack/use-send-notification-to-slack';
import useSendEmailNotification from './send-email/use-send-email-notification';
import useSendTeamsNotification from './send-teams-notification/use-send-teams-notification';
import { useAzureProvider } from 'src/azure/context';

const initialAction: ActionItemType = {
	id: '',
	action: '',
	editWorkItem: null,
	createdAt: new Date(),
	addCommentToWorkItem: null,
	sendNotificationToSlack: null,
	sendEmailNotification: null,
	sendTeamsNotification: null,
};

const AutomationRuleActionContext = createContext({} as AutomationRuleActionContextType);
export const useAutomationRuleActions = () => useContext(AutomationRuleActionContext);

export default function AutomationRuleActionProvider(props: { children?: ReactNode | Iterable<ReactNode>; }) {
	const [actions, setActions] = useState<Array<ActionItemType>>([]);
	const [actionValidationErrors, setActionValidationErrors] = useState<Array<ActionValidationErrors>>([]);

	const {applicationContext} = useAzureProvider();

	const editWorkItemHook = useEditWorkItem();
	const addCommentToWiHook = useAddCommentToWorkItem();
	const sendNotificationToSlackHook = useSendNotificationToSlack();
	const sendEmailNotificaitonHook = useSendEmailNotification();
	const sendTeamsNotificationHook = useSendTeamsNotification();

	const addNewAction = useCallback(() => {
		setActions(oldValues => [
			...oldValues,
			{
				id: randomStringGenerator(),
				action: '',
				editWorkItem: null,
				createdAt: new Date(),
				addCommentToWorkItem: null,
				sendNotificationToSlack: null,
				sendEmailNotification: null,
				sendTeamsNotification: null,
			}
		]);
	}, []);

	const validate = useCallback(async () => {
		let hasError = false;
		const validationErrors: Array<ActionValidationErrors> = [];

		const errorToActionItem = (actionItem: ActionItemType) => {
			return [
				stringRequired(actionItem.action as string, 'This field is required'),
			];
		};

		await Promise.all(
			actions.map((actionItem) => new Promise((resolve) => {
				const actionError = errorToActionItem(actionItem);
				const errorMsg = actionError.find(item => item);
	
				if (errorMsg) {
					hasError = true;
					validationErrors.push({
						actionId: actionItem.id,
						action: errorMsg, editWorkItem: null,
						addCommentToWorkItem: null,
						sendNotificationToSlack: null,
						sendEmailNotification: null,
						sendTeamsNotification: null,
					});
	
					resolve(null);
				}

				const mapValidations = new Map()
					.set(actionTypes.sendMessageToTeams, () => {
						const values = actionItem.sendTeamsNotification as SendTeamsNotificationActionType;
						const results = sendTeamsNotificationHook.validate({
							webHookUrl: values.webHookUrl,
							text: values.text,
						});

						if (results.hasErrors()) {
							hasError = true;
							validationErrors.push({
								action: null,
								actionId: actionItem.id,
								sendNotificationToSlack: null,
								editWorkItem: null,
								addCommentToWorkItem: null,
								sendEmailNotification: null,
								sendTeamsNotification: results.errors,
							});
						}

						resolve(null);
					})
					.set(actionTypes.sendEmailNotification, () => {
						const values = actionItem.sendEmailNotification as SendEmailNotificationActionType;
						const result = sendEmailNotificaitonHook.validate({
							to: values.to,
							subject: values.subject,
							text: values.text,
						});

						if (result.hasErrors()) {
							hasError = true;
							validationErrors.push({
								action: null,
								actionId: actionItem.id,
								sendNotificationToSlack: null,
								editWorkItem: null,
								addCommentToWorkItem: null,
								sendTeamsNotification: null,
								sendEmailNotification: result.errors,
							});
						}

						resolve(null);
					})
					.set(actionTypes.sendNotificationToSlack, () => {
						const values = actionItem.sendNotificationToSlack as SendNotificationToSlackActionType;
						const result = sendNotificationToSlackHook.validate({
							webHookUrl: values.webHookUrl,
							text: values.text
						});

						if (result.hasErrors()) {
							hasError = true;
							validationErrors.push({
								action: null,
								actionId: actionItem.id,
								sendNotificationToSlack: result.errors,
								editWorkItem: null,
								addCommentToWorkItem: null,
								sendEmailNotification: null,
								sendTeamsNotification: null,
							});
						}

						resolve(null);
					})
					.set(actionTypes.addCommentToWorkItem, async () => {
						const result = addCommentToWiHook.validate({
							commentValue: actionItem!.addCommentToWorkItem!.commentValue,
						});
	
						if (result.hasErrors()) {
							hasError = true;
							validationErrors.push({
								action: null,
								actionId: actionItem.id,
								editWorkItem: null,
								sendNotificationToSlack: null,
								addCommentToWorkItem: result.errors,
								sendEmailNotification: null,
								sendTeamsNotification: null,
							});
						}

						resolve(null);
					})
					.set(actionTypes.editWorkItem, () => {
						const editWorkItemAction = actionItem!.editWorkItem as EditWorkItemActionType;
						const result = editWorkItemHook.validate(editWorkItemAction);

						if (result.hasErrors()) {
							hasError = true;
							validationErrors.push({
								action: null,
								actionId: actionItem.id,
								editWorkItem: result.errors,
								addCommentToWorkItem: null,
								sendNotificationToSlack: null,
								sendEmailNotification: null,
								sendTeamsNotification: null,
							});
						}

						resolve(null);
					})
					.get(actionItem.action);

				mapValidations && mapValidations();
			}))
		);

		setActionValidationErrors(validationErrors);
		return hasError;
	}, [actions]);

	const resetValidationErrors = useCallback(() => {
		setActionValidationErrors([]);
	}, []);

	const getValues = useCallback(() => {
		const dataMap = new Map()
			.set(actionTypes.editWorkItem, (action: ActionItemType) => {
				return editWorkItemHook.getValues(action?.editWorkItem as EditWorkItemActionType);
			})
			.set(actionTypes.addCommentToWorkItem, (action: ActionItemType) => {
				return addCommentToWiHook.getValues(action.addCommentToWorkItem as AddCommentToWorkItemActionType);
			})
			.set(actionTypes.sendEmailNotification, (action: ActionItemType) => {
				return sendEmailNotificaitonHook.getValues(action.sendEmailNotification as SendEmailNotificationActionType);
			})
			.set(actionTypes.sendMessageToTeams, (action: ActionItemType) => {
				return sendTeamsNotificationHook.getValues(action.sendTeamsNotification as SendTeamsNotificationActionType);
			})
			.set(actionTypes.sendNotificationToSlack, (action: ActionItemType) => {
				return sendNotificationToSlackHook.getValues(action.sendNotificationToSlack as SendNotificationToSlackActionType);
			});

		return actions.map((item) => {
			const findData = dataMap.get(item.action);
			return {
				type: item.action as string,
				data: findData ? findData(item) : null,
			};
		});
	}, [actions]);

	const reset = useCallback(() => {
		setActions([]);
	}, []);

	const loadActions = useCallback(async (loadedConditions: Array<{
			type: string;
			values: any;
		}>
	) => {
		const loadValuesMap = new Map()
			.set(actionTypes.editWorkItem, async (
				actionType: string, data: {
					field: string;
					action: string;
					relationship: string;
					value: any;
				}, properties: { id: string; created_at: Date }): Promise<ActionItemType> => {
				return {
					...initialAction,
					id: properties.id,
					createdAt: properties.created_at,
					action: actionType,
					editWorkItem: await editWorkItemHook.loadValues(data)
				};
			})
			.set(actionTypes.addCommentToWorkItem, async (actionType: string, data: AddCommentInputDataType, properties: { id: string; created_at: Date }): Promise<ActionItemType> => {
				return {
					...initialAction,
					id: properties.id,
					createdAt: properties.created_at,
					action: actionType,
					addCommentToWorkItem: addCommentToWiHook.loadValues(data)
				};
			})
			.set(actionTypes.sendEmailNotification, async (actionType: string, data: SendEmailNotificationInputDataType, properties: { id: string; created_at: Date }): Promise<ActionItemType> => {
				return {
					...initialAction,
					id: properties.id,
					createdAt: properties.created_at,
					action: actionType,
					sendEmailNotification: await sendEmailNotificaitonHook.loadValues(data),
				};
			})
			.set(actionTypes.sendNotificationToSlack, async (actionType: string, data: SendSlackNotificationInputDataType, properties: { id: string; created_at: Date }): Promise<ActionItemType> => {
				return {
					...initialAction,
					id: properties.id,
					createdAt: properties.created_at,
					action: actionType,
					sendNotificationToSlack: sendNotificationToSlackHook.loadValues(data),
				};
			})
			.set(actionTypes.sendMessageToTeams, async (actionType: string, data: SendTeamsNotificationInputDataType, properties: { id: string; created_at: Date }): Promise<ActionItemType> => {
				return {
					...initialAction,
					id: properties.id,
					createdAt: properties.created_at,
					action: actionType,
					sendTeamsNotification: sendTeamsNotificationHook.loadValues(data),
				};
			});

		const results = await Promise.all(loadedConditions.map(async (item, idx) => {
			const getData = loadValuesMap.get(item.type);
			const now = new Date();
			return await getData(
				item.type,
				item.values,
				{
					id: randomStringGenerator(),
					created_at: new Date(now.getTime() + idx * 1000),
				});
		}));
	
		setActions(results);
	}, [applicationContext]);

	const removeActionItem = useCallback((id: string) => {
		setActions(oldActions => [
			...oldActions.filter(item => item.id !== id)
		]);
	}, []);

	return (
		<AutomationRuleActionContext.Provider value={{
			actions,
			setActions,
			addNewAction,
			validate,
			actionValidationErrors,
			resetValidationErrors,
			getValues,
			reset,
			loadActions,
			removeActionItem
		}}>
			{props?.children}
		</AutomationRuleActionContext.Provider>
	);
}
